import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { seo } from "@/utils/seo";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  AlertTriangle,
  CheckCircle2,
  Database,
  HeartPulse,
  Lock,
  Mail,
  Scale,
  Shield,
  ShieldAlert,
  ShieldCheck,
  Video,
} from "lucide-react";
import { z } from "zod";

const privacySearchSchema = z.object({
  tab: z
    .enum(["kvkk", "videos", "privacy", "terms"])
    .optional()
    .default("kvkk"),
});

export const Route = createFileRoute("/_app/privacy")({
  validateSearch: privacySearchSchema,
  head: () => ({
    meta: seo({
      title: "Gizlilik Politikası, KVKK ve Video İzinleri | AtalayTheCoach",
      description:
        "AtalayTheCoach Kişisel Verilerin Korunması (KVKK) Aydınlatma Metni, Antrenman Form Videoları Açık Rıza Şartları, Gizlilik Politikası ve Kullanım Koşulları.",
      canonical: "https://atalaythecoach.com/privacy",
    }),
  }),
  component: PrivacyPage,
});

function PrivacyPage() {
  const { tab } = Route.useSearch();
  const navigate = useNavigate();

  const handleTabChange = (newTab: string) => {
    navigate({
      to: "/privacy",
      search: { tab: newTab as "kvkk" | "videos" | "privacy" | "terms" },
      replace: true,
    });
  };

  return (
    <div className="min-h-screen bg-background pt-24 pb-20">
      {/* Hero Header */}
      <div className="border-border/40 border-b bg-gradient-to-b from-muted/40 via-background to-background py-10">
        <div className="container mx-auto max-w-4xl px-4 text-center">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl border border-primary/20 bg-primary/10 text-primary shadow-sm">
            <ShieldCheck className="h-7 w-7" />
          </div>
          <Badge
            variant="outline"
            className="mb-3 border-primary/30 bg-primary/5 font-semibold text-primary text-xs uppercase tracking-wider"
          >
            Yasal Bilgilendirme & Güvenlik
          </Badge>
          <h1 className="font-extrabold text-3xl text-foreground tracking-tight sm:text-4xl">
            KVKK, Gizlilik ve Video İzinleri
          </h1>
          <p className="mx-auto mt-3 max-w-2xl text-muted-foreground text-sm leading-relaxed sm:text-base">
            AtalayTheCoach olarak kişisel verilerinizin, antrenman
            kayıtlarınızın ve form analizi videolarınızın gizliliğine ve
            güvenliğine en yüksek düzeyde önem veriyoruz.
          </p>
          <div className="mt-4 flex items-center justify-center gap-3 text-muted-foreground text-xs">
            <span>Versiyon: 1.0</span>
            <span>•</span>
            <span>Son Güncelleme: 24 Ağustos 2026</span>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="container mx-auto max-w-4xl px-4 pt-8">
        <Tabs
          value={tab}
          onValueChange={handleTabChange}
          className="w-full space-y-8"
        >
          {/* Navigation Tabs */}
          <div className="no-scrollbar overflow-x-auto pb-2">
            <TabsList className="grid h-auto w-full grid-cols-2 gap-1.5 rounded-2xl border border-border/50 bg-muted/40 p-1.5 md:grid-cols-4">
              <TabsTrigger
                value="kvkk"
                className="flex items-center justify-center gap-2 rounded-xl py-3 font-semibold text-xs transition-all data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm sm:text-sm"
              >
                <Scale className="h-4 w-4 text-primary" />
                <span>KVKK Aydınlatma</span>
              </TabsTrigger>
              <TabsTrigger
                value="videos"
                className="flex items-center justify-center gap-2 rounded-xl py-3 font-semibold text-xs transition-all data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm sm:text-sm"
              >
                <Video className="h-4 w-4 text-primary" />
                <span>Video & Açık Rıza</span>
              </TabsTrigger>
              <TabsTrigger
                value="privacy"
                className="flex items-center justify-center gap-2 rounded-xl py-3 font-semibold text-xs transition-all data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm sm:text-sm"
              >
                <Lock className="h-4 w-4 text-primary" />
                <span>Gizlilik Politikası</span>
              </TabsTrigger>
              <TabsTrigger
                value="terms"
                className="flex items-center justify-center gap-2 rounded-xl py-3 font-semibold text-xs transition-all data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm sm:text-sm"
              >
                <HeartPulse className="h-4 w-4 text-primary" />
                <span>Kullanım & Sağlık</span>
              </TabsTrigger>
            </TabsList>
          </div>

          {/* TAB 1: KVKK Aydınlatma Metni */}
          <TabsContent value="kvkk" className="space-y-6 outline-none">
            <Card className="border-border/60 shadow-sm">
              <CardHeader className="border-b bg-muted/20 pb-6">
                <div className="flex items-center gap-3">
                  <div className="rounded-xl bg-primary/10 p-2.5 text-primary">
                    <Scale className="h-6 w-6" />
                  </div>
                  <div>
                    <CardTitle className="text-xl">
                      KVKK Aydınlatma Metni
                    </CardTitle>
                    <p className="mt-1 text-muted-foreground text-xs">
                      6698 Sayılı Kişisel Verilerin Korunması Kanunu ("KVKK")
                      Kapsamında Bilgilendirme
                    </p>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-6 pt-6 text-foreground/90 text-sm leading-relaxed">
                <div className="rounded-xl border border-primary/20 bg-primary/5 p-4 text-xs sm:text-sm">
                  <p className="font-semibold text-foreground">
                    Veri Sorumlusu:{" "}
                    <span className="font-normal text-muted-foreground">
                      Alican Atalay (AtalayTheCoach)
                    </span>
                  </p>
                  <p className="mt-1 font-semibold text-foreground">
                    İletişim:{" "}
                    <a
                      href="mailto:info@atalaythecoach.com"
                      className="font-normal text-primary hover:underline"
                    >
                      info@atalaythecoach.com
                    </a>
                  </p>
                  <p className="mt-1 font-semibold text-foreground">
                    Web Sitesi:{" "}
                    <span className="font-normal text-muted-foreground">
                      atalaythecoach.com
                    </span>
                  </p>
                </div>

                <div className="space-y-3">
                  <h3 className="font-bold text-base text-foreground">
                    1. Kişisel Verilerin İşlenme Amacı
                  </h3>
                  <p>
                    Kişisel verileriniz, 6698 sayılı Kanun’un 5. ve 6.
                    maddelerinde belirtilen kişisel veri işleme şartları ve
                    amaçları dahilinde, aşağıdaki amaçlarla işlenmektedir:
                  </p>
                  <ul className="list-inside list-disc space-y-1.5 pl-2 text-muted-foreground text-xs sm:text-sm">
                    <li>
                      Kişiselleştirilmiş antrenman, powerlifting ve güç
                      programlarının hazırlanması ve sunulması,
                    </li>
                    <li>
                      Antrenman takip sistemi üzerinden set, tekrar, RPE ve 1RM
                      (maksimum ağırlık) hesaplamalarının yapılması,
                    </li>
                    <li>
                      Egzersiz form analizi ve teknik koçluk
                      değerlendirmelerinin yürütülmesi,
                    </li>
                    <li>
                      Kullanıcı hesabı oluşturulması, kimlik doğrulama ve oturum
                      güvenliğinin temini,
                    </li>
                    <li>
                      Antrenman hatırlatıcıları, program güncellemeleri ve PWA
                      bildirimlerinin iletilmesi,
                    </li>
                    <li>Yasal ve idari yükümlülüklerin yerine getirilmesi.</li>
                  </ul>
                </div>

                <Separator />

                <div className="space-y-3">
                  <h3 className="font-bold text-base text-foreground">
                    2. İşlenen Kişisel Veri Kategorileri
                  </h3>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div className="rounded-xl border border-border/60 bg-card p-3.5">
                      <p className="font-bold text-foreground text-xs uppercase tracking-wider">
                        Kimlik & İletişim
                      </p>
                      <p className="mt-1 text-muted-foreground text-xs">
                        Ad, soyad, kullanıcı adı, e-posta adresi, profil
                        fotoğrafı.
                      </p>
                    </div>
                    <div className="rounded-xl border border-border/60 bg-card p-3.5">
                      <p className="font-bold text-foreground text-xs uppercase tracking-wider">
                        Antrenman & Fiziksel Performans
                      </p>
                      <p className="mt-1 text-muted-foreground text-xs">
                        1RM maksimum ağırlıklar, set/tekrar/ağırlık/RPE
                        kayıtları, günlük antrenman metrikleri, antrenman
                        notları.
                      </p>
                    </div>
                    <div className="rounded-xl border border-border/60 bg-card p-3.5">
                      <p className="font-bold text-foreground text-xs uppercase tracking-wider">
                        Görsel & Video Kayıtları
                      </p>
                      <p className="mt-1 text-muted-foreground text-xs">
                        Form analizi ve teknik koçluk için yüklediğiniz egzersiz
                        videoları ve video önizleme kareleri (thumbnail).
                      </p>
                    </div>
                    <div className="rounded-xl border border-border/60 bg-card p-3.5">
                      <p className="font-bold text-foreground text-xs uppercase tracking-wider">
                        İşlem Güvenliği
                      </p>
                      <p className="mt-1 text-muted-foreground text-xs">
                        IP adresi, oturum belirteçleri (cookies/tokens), cihaz
                        bilgisi, giriş logları.
                      </p>
                    </div>
                  </div>
                </div>

                <Separator />

                <div className="space-y-3">
                  <h3 className="font-bold text-base text-foreground">
                    3. Kişisel Verilerin Aktarılması
                  </h3>
                  <p className="text-muted-foreground text-xs sm:text-sm">
                    Kişisel verileriniz, kanuni zorunluluklar hariç olmak üzere
                    ve platformun güvenli şekilde çalışabilmesi için yalnızca
                    aşağıda belirtilen altyapı hizmet sağlayıcıları ile veri
                    güvenliği taahhütleri kapsamında paylaşılmaktadır:
                  </p>
                  <ul className="list-inside list-disc space-y-1 pl-2 text-muted-foreground text-xs sm:text-sm">
                    <li>
                      <strong>AWS (Amazon Web Services)</strong>: Güvenli bulut
                      depolama (S3) ve sunucu altyapısı (Lambda).
                    </li>
                    <li>
                      <strong>Turso / LibSQL</strong>: Şifrelenmiş veritabanı
                      altyapısı.
                    </li>
                    <li>
                      <strong>Resend</strong>: Oturum ve şifre sıfırlama e-posta
                      iletimi.
                    </li>
                  </ul>
                  <p className="font-medium text-foreground text-xs sm:text-sm">
                    Verileriniz kesinlikle reklam, pazarlama amacıyla üçüncü
                    taraflarla paylaşılmaz veya satılmaz.
                  </p>
                </div>

                <Separator />

                <div className="space-y-3">
                  <h3 className="font-bold text-base text-foreground">
                    4. KVKK Madde 11 Uyarınca Haklarınız
                  </h3>
                  <p className="text-muted-foreground text-xs sm:text-sm">
                    Kanun kapsamında veri sahibi olarak aşağıdaki haklara
                    sahipsiniz:
                  </p>
                  <div className="grid gap-2 text-xs sm:grid-cols-2">
                    <div className="flex items-start gap-2">
                      <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                      <span>
                        Kişisel verilerinizin işlenip işlenmediğini öğrenme
                      </span>
                    </div>
                    <div className="flex items-start gap-2">
                      <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                      <span>İşlenmişse buna ilişkin bilgi talep etme</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                      <span>
                        İşlenme amacını ve amacına uygun kullanılıp
                        kullanılmadığını öğrenme
                      </span>
                    </div>
                    <div className="flex items-start gap-2">
                      <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                      <span>
                        Eksik veya yanlış işlenmişse düzeltilmesini isteme
                      </span>
                    </div>
                    <div className="flex items-start gap-2">
                      <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                      <span>
                        Verilerin silinmesini veya yok edilmesini talep etme
                      </span>
                    </div>
                    <div className="flex items-start gap-2">
                      <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                      <span>
                        Yüklediğiniz videoların sistemden kaldırılmasını isteme
                      </span>
                    </div>
                  </div>
                  <p className="pt-2 text-muted-foreground text-xs">
                    Haklarınızı kullanmak için{" "}
                    <a
                      href="mailto:info@atalaythecoach.com"
                      className="font-semibold text-primary underline"
                    >
                      info@atalaythecoach.com
                    </a>{" "}
                    adresine e-posta gönderebilirsiniz. Talepleriniz en geç 30
                    gün içinde ücretsiz olarak sonuçlandırılacaktır.
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* TAB 2: Video & Açık Rıza Metni */}
          <TabsContent value="videos" className="space-y-6 outline-none">
            <Card className="border-border/60 shadow-sm">
              <CardHeader className="border-b bg-muted/20 pb-6">
                <div className="flex items-center gap-3">
                  <div className="rounded-xl bg-primary/10 p-2.5 text-primary">
                    <Video className="h-6 w-6" />
                  </div>
                  <div>
                    <CardTitle className="text-xl">
                      Antrenman Videoları ve Form Analizi Açık Rıza Beyanı
                    </CardTitle>
                    <p className="mt-1 text-muted-foreground text-xs">
                      Egzersiz Kayıtları, Görsel Veriler ve Biyomekanik Koçluk
                      Değerlendirmesi İzinleri
                    </p>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-6 pt-6 text-foreground/90 text-sm leading-relaxed">
                {/* Critical Video Banner */}
                <div className="rounded-2xl border border-primary/30 bg-primary/5 p-5">
                  <div className="flex items-start gap-3">
                    <ShieldAlert className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
                    <div className="space-y-1.5">
                      <h4 className="font-bold text-foreground text-sm">
                        Antrenman Videolarınızın Gizlilik Garantisi
                      </h4>
                      <p className="text-muted-foreground text-xs leading-relaxed">
                        Sisteme yüklediğiniz egzersiz videoları (Squat, Bench
                        Press, Deadlift vb. form kayıtları){" "}
                        <strong>YALNIZCA</strong> teknik form analizi, sakatlık
                        riskinin önlenmesi ve bireysel antrenman geri
                        bildiriminin verilmesi amacıyla işlenir.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  <h3 className="font-bold text-base text-foreground">
                    1. Videoların Toplanma Amacı ve İşleniş Şekli
                  </h3>
                  <p className="text-muted-foreground text-xs sm:text-sm">
                    Kuvvet sporlarında hareket formunun doğru icra edilmesi, hem
                    maksimum güç üretimi hem de eklem/omurga sağlığının
                    korunması açısından hayati önem taşır. Yüklediğiniz
                    videolar:
                  </p>
                  <ul className="list-inside list-disc space-y-1.5 pl-2 text-muted-foreground text-xs sm:text-sm">
                    <li>
                      Koçunuz tarafından hareket tekniğinizin (bar hızı, bar
                      yolu, derinlik, duruş açısı vb.) incelenmesi,
                    </li>
                    <li>
                      Teknik hataların tespit edilerek programın buna göre
                      revize edilmesi,
                    </li>
                    <li>
                      Geçmiş antrenmanlarınız ile kıyaslanarak form
                      gelişiminizin takip edilmesi amacıyla işlenir.
                    </li>
                  </ul>
                </div>

                <Separator />

                <div className="space-y-3">
                  <h3 className="font-bold text-base text-foreground">
                    2. Kesin Paylaşım Yasağı (Gizlilik Taahhüdü)
                  </h3>
                  <div className="rounded-xl border border-destructive/20 bg-destructive/5 p-4 text-xs sm:text-sm">
                    <p className="font-bold text-destructive">
                      Önemli Güvence:
                    </p>
                    <p className="mt-1 text-muted-foreground">
                      Kullanıcının <strong>açık, yazılı ve özel izni</strong>{" "}
                      olmaksızın, yüklenen hiçbir video, ekran görüntüsü veya
                      form analizi görseli:
                    </p>
                    <ul className="mt-2 list-inside list-disc space-y-1 pl-2 text-muted-foreground">
                      <li>
                        Instagram, YouTube, TikTok veya herhangi bir sosyal
                        medya platformunda,
                      </li>
                      <li>
                        Web sitesinin herkese açık alanlarında veya
                        reklam/tanıtım materyallerinde,
                      </li>
                      <li>
                        Üçüncü şahıslar veya diğer platform kullanıcıları ile,
                      </li>
                    </ul>
                    <p className="mt-2 font-semibold text-foreground">
                      KESİNLİKLE PAYLAŞILMAZ VE YAYINLANMAZ.
                    </p>
                  </div>
                </div>

                <Separator />

                <div className="space-y-3">
                  <h3 className="font-bold text-base text-foreground">
                    3. Depolama Güvenliği ve Silme Hakkı
                  </h3>
                  <p className="text-muted-foreground text-xs sm:text-sm">
                    Yüklenen videolar, AWS S3 bulut depolama sunucularında
                    yüksek güvenlikli erişim denetimi protokolleri altında
                    saklanır.
                  </p>
                  <div className="space-y-2 text-muted-foreground text-xs sm:text-sm">
                    <p>
                      • <strong>İmha ve Silme:</strong> Kullanıcı, dilediği
                      zaman kendi panelinden ilgili antrenman setinin videosunu
                      silebilir veya e-posta yoluyla tüm video arşivinin kalıcı
                      olarak silinmesini talep edebilir.
                    </p>
                    <p>
                      • <strong>Hesap Kapatma:</strong> Üyelik
                      sonlandırıldığında veya hesap silme talebinde
                      bulunulduğunda, kullanıcıya ait tüm video dosyaları AWS S3
                      sunucularından geri döndürülemez şekilde silinir.
                    </p>
                  </div>
                </div>

                <Separator />

                <div className="space-y-3">
                  <h3 className="font-bold text-base text-foreground">
                    4. Açık Rıza Beyanı
                  </h3>
                  <p className="rounded-xl border border-border/80 bg-muted/30 p-4 font-mono text-muted-foreground text-xs leading-relaxed">
                    "AtalayTheCoach platformuna antrenman takibi ve teknik form
                    analizi amacıyla yüklediğim egzersiz videolarımın, görsel
                    kayıtlarımın ve fiziksel gelişim verilerimin; yukarıda
                    belirtilen amaçlarla koçluk ve analiz kapsamında
                    işlenmesine, güvenli sunucularda saklanmasına ve tarafıma
                    teknik geri bildirim verilmesine özgür irademle AÇIK RIZA
                    VERİYORUM."
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* TAB 3: Gizlilik Politikası */}
          <TabsContent value="privacy" className="space-y-6 outline-none">
            <Card className="border-border/60 shadow-sm">
              <CardHeader className="border-b bg-muted/20 pb-6">
                <div className="flex items-center gap-3">
                  <div className="rounded-xl bg-primary/10 p-2.5 text-primary">
                    <Lock className="h-6 w-6" />
                  </div>
                  <div>
                    <CardTitle className="text-xl">
                      Gizlilik Politikası (Privacy Policy)
                    </CardTitle>
                    <p className="mt-1 text-muted-foreground text-xs">
                      Çerezler, Veri Güvenliği, Entegrasyonlar ve Kullanıcı
                      Hakları Politikası
                    </p>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-6 pt-6 text-foreground/90 text-sm leading-relaxed">
                <div className="space-y-3">
                  <h3 className="font-bold text-base text-foreground">
                    1. Çerezler (Cookies) ve Yerel Depolama (Local Storage)
                  </h3>
                  <p className="text-muted-foreground text-xs sm:text-sm">
                    Platformumuz, size güvenli ve kesintisiz bir deneyim sunmak
                    amacıyla zorunlu oturum çerezleri ve yerel depolama
                    teknolojileri kullanmaktadır:
                  </p>
                  <ul className="list-inside list-disc space-y-1 pl-2 text-muted-foreground text-xs sm:text-sm">
                    <li>
                      <strong>Oturum Çerezleri (Session Cookies):</strong> Giriş
                      yaptığınızda kimliğinizi güvenle doğrulamak için HTTP-Only
                      şifrelenmiş çerezler kullanılır.
                    </li>
                    <li>
                      <strong>Tercih Depolaması:</strong> Koyu/Açık tema
                      tercihiniz ve PWA çevrimdışı önbelleği tarayıcınızın yerel
                      hafızasında saklanır.
                    </li>
                    <li>
                      <strong>Reklam Çerezi Bulunmaz:</strong> Platformumuzda
                      kullanıcıları üçüncü taraf sitelerde takip eden reklam
                      veya pazarlama çerezleri kullanılmaz.
                    </li>
                  </ul>
                </div>

                <Separator />

                <div className="space-y-3">
                  <h3 className="font-bold text-base text-foreground">
                    2. Google OAuth ile Giriş
                  </h3>
                  <p className="text-muted-foreground text-xs sm:text-sm">
                    Google ile Giriş Yap seçeneğini kullandığınızda, yalnızca
                    Google hesap profilinizden ad, soyad ve e-posta bilginiz
                    temin edilir. Google şifrenize veya diğer özel hesap
                    verilerinize erişilmez.
                  </p>
                </div>

                <Separator />

                <div className="space-y-3">
                  <h3 className="font-bold text-base text-foreground">
                    3. Veri Güvenliği Önlemleri
                  </h3>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div className="flex items-start gap-2.5 rounded-xl border border-border/50 bg-muted/20 p-3">
                      <Shield className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                      <div>
                        <p className="font-semibold text-foreground text-xs">
                          SSL/TLS Şifreleme
                        </p>
                        <p className="text-muted-foreground text-xs">
                          Tüm veri transferi 256-bit SSL/TLS protokolü ile
                          şifrelenir.
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-2.5 rounded-xl border border-border/50 bg-muted/20 p-3">
                      <Database className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                      <div>
                        <p className="font-semibold text-foreground text-xs">
                          Şifrelenmiş Depolama
                        </p>
                        <p className="text-muted-foreground text-xs">
                          Veritabanı ve S3 medya dosyaları şifrelenmiş
                          sunucularda korunur.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <Separator />

                <div className="space-y-3">
                  <h3 className="font-bold text-base text-foreground">
                    4. İletişim ve Politika Güncellemeleri
                  </h3>
                  <p className="text-muted-foreground text-xs sm:text-sm">
                    Gizlilik politikamızda mevzuat değişiklikleri veya yeni
                    platform özellikleri doğrultusunda güncellemeler
                    yapılabilir. Önemli değişiklikler kullanıcılara platform içi
                    bildirimler veya onay pencereleri aracılığıyla duyurulur.
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* TAB 4: Kullanım Koşulları & Sağlık Sorumluluk Reddi */}
          <TabsContent value="terms" className="space-y-6 outline-none">
            <Card className="border-border/60 shadow-sm">
              <CardHeader className="border-b bg-muted/20 pb-6">
                <div className="flex items-center gap-3">
                  <div className="rounded-xl bg-primary/10 p-2.5 text-primary">
                    <HeartPulse className="h-6 w-6" />
                  </div>
                  <div>
                    <CardTitle className="text-xl">
                      Kullanım Koşulları ve Sağlık Beyanı
                    </CardTitle>
                    <p className="mt-1 text-muted-foreground text-xs">
                      Platform Kullanım Kuralları, Egzersiz Güvenliği ve Sağlık
                      Sorumluluk Reddi
                    </p>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-6 pt-6 text-foreground/90 text-sm leading-relaxed">
                {/* Health Alert Box */}
                <div className="rounded-2xl border border-amber-500/30 bg-amber-500/5 p-5">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-500" />
                    <div className="space-y-1.5">
                      <h4 className="font-bold text-foreground text-sm">
                        Sağlık ve Fiziksel Yeterlilik Beyanı
                      </h4>
                      <p className="text-muted-foreground text-xs leading-relaxed">
                        Ağırlık antrenmanları, powerlifting ve yüksek yoğunluklu
                        fiziksel egzersizler kardiyovasküler sistem, kas ve
                        iskelet sistemi üzerinde yüksek efor gerektirir.
                        Programa başlamadan önce hekim kontrolünden geçmiş
                        olmanız gerekmektedir.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  <h3 className="font-bold text-base text-foreground">
                    1. Tıbbi Tavsiye Niteliği Taşımama
                  </h3>
                  <p className="text-muted-foreground text-xs sm:text-sm">
                    AtalayTheCoach platformunda sunulan antrenman programları,
                    RPE tabloları, hesaplayıcılar, yazılar ve koçluk önerileri;
                    tıbbi teşhis, tedavi veya tıbbi reçete niteliği taşımaz.
                    Herhangi bir kronik rahatsızlığınız, kalp-damar probleminiz,
                    ortopedik sakatlığınız veya hamilelik durumunuz varsa
                    egzersiz yapmadan önce mutlaka uzman bir hekime danışınız.
                  </p>
                </div>

                <Separator />

                <div className="space-y-3">
                  <h3 className="font-bold text-base text-foreground">
                    2. Sorumluluğun Kabulü
                  </h3>
                  <p className="text-muted-foreground text-xs sm:text-sm">
                    Kullanıcı, antrenman programlarını uygularken kendi fiziksel
                    limitlerini gözetmekle, emniyet barları ve güvenlik
                    ekipmanlarını kullanmakla yükümlüdür. Egzersizlerin hatalı
                    icrasından, aşırı yüklenmeden veya hekim tavsiyelerine
                    uyulmamasından kaynaklanabilecek sakatlık ve sağlık
                    sorunlarından platform veya antrenör sorumlu tutulamaz.
                  </p>
                </div>

                <Separator />

                <div className="space-y-3">
                  <h3 className="font-bold text-base text-foreground">
                    3. Hesap Güvenliği ve Fikri Mülkiyet
                  </h3>
                  <ul className="list-inside list-disc space-y-1.5 pl-2 text-muted-foreground text-xs sm:text-sm">
                    <li>
                      Kullanıcı, hesap bilgilerini ve özel program erişim
                      kodlarını üçüncü şahıslarla paylaşamaz.
                    </li>
                    <li>
                      Platformda yer alan antrenman programları, algoritmalar,
                      metinler ve içeriklerin tüm telif hakları AtalayTheCoach'a
                      aittir; izinsiz kopyalanamaz veya çoğaltılamaz.
                    </li>
                  </ul>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Support & Contact Card */}
        <div className="mt-8 rounded-2xl border border-border/60 bg-muted/20 p-6 text-center">
          <h4 className="font-bold text-base text-foreground">
            Sorularınız veya Veri Talepleriniz mi Var?
          </h4>
          <p className="mt-1 text-muted-foreground text-xs sm:text-sm">
            KVKK, gizlilik veya antrenman video kayıtlarınıza ilişkin her türlü
            soru veya silme talebiniz için doğrudan iletişime geçebilirsiniz.
          </p>
          <div className="mt-4 flex justify-center">
            <Button
              asChild
              variant="outline"
              size="sm"
              className="gap-2 rounded-xl"
            >
              <a href="mailto:info@atalaythecoach.com">
                <Mail className="h-4 w-4 text-primary" />
                info@atalaythecoach.com
              </a>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
